import React, { useRef } from 'react';
import T from 'prop-types';
// components
import { MentionsInput as ReactMentionsInput, Mention } from 'react-mentions';
// utils
import cn from 'classnames';
// styles
import './NameTokenInput.scss';

export const NAME_TOKEN = '{name}';
const TOKEN_ID = 'name';
// A control character no one can type marks the pill inside the editor, so hand-typed braces stay plain text
const MARK = '\u0001';
const MARKUP = `${MARK}__id__${MARK}`;
const INTERNAL_TOKEN = `${MARK}${TOKEN_ID}${MARK}`;

const toEditorValue = (value) => value.split(NAME_TOKEN).join(INTERNAL_TOKEN);
const toStoredValue = (value) => value.split(INTERNAL_TOKEN).join(NAME_TOKEN);

// Single-line text with one non-editable "first name" pill, stored as a {name} token.
// The pill is added with the button, by typing "@" and picking it from the list, or by typing {name}
const NameTokenInput = ({ value, onChange, tokenLabel, addLabel, placeholder, disabled, maxLength }) => {
  const inputRef = useRef();
  const hasToken = value.includes(NAME_TOKEN);
  const tokenData = [{ id: TOKEN_ID, display: tokenLabel }];

  // en spaces inside the pill act as its padding: the ring stays clear of the neighbouring
  // comma and period while the highlighter and the input stay aligned; the stored value is still {name}
  const displayTransform = () => `\u2002${tokenLabel}\u2002`;

  const handleChange = (event, newValue, newPlainText) => {
    if (maxLength && newPlainText.length > maxLength) return;
    onChange(toStoredValue(newValue));
  };

  const handleAddToken = () => {
    const base = value.replace(/\s+$/, '');
    onChange(base ? `${base} ${NAME_TOKEN}` : NAME_TOKEN);
    inputRef.current?.wrappedInstance?.refs?.input?.focus();
  };

  return (
    <div className={cn('NameTokenInput', { disabled })}>
      <ReactMentionsInput
        singleLine
        value={toEditorValue(value)}
        onChange={handleChange}
        markup={MARKUP}
        displayTransform={displayTransform}
        placeholder={placeholder}
        className="NameTokenInput__field"
        disabled={disabled}
        ref={inputRef}
      >
        <Mention
          trigger="@"
          data={tokenData}
          renderSuggestion={(suggestion) => <span className="semibold">{suggestion.display}</span>}
        />
      </ReactMentionsInput>
      {!hasToken && (
        <button type="button" className="add-token" onClick={handleAddToken} disabled={disabled}>
          {addLabel}
        </button>
      )}
    </div>
  );
};

NameTokenInput.propTypes = {
  value: T.string.isRequired,
  onChange: T.func.isRequired,
  tokenLabel: T.string.isRequired,
  addLabel: T.string.isRequired,
  placeholder: T.string,
  disabled: T.bool,
  maxLength: T.number,
};

NameTokenInput.defaultProps = {
  placeholder: '',
  disabled: false,
  maxLength: 0,
};

export default NameTokenInput;
